<template>
  <div
    ref="container"
    class="resizer-container"
    v-show="isReady"
    :class="{
      resizing,
    }"
    :style="cssVars"
  >
    <main class="main" @mousedown="mouseDownDrag">
      <slot></slot>
    </main>
    <div
      class="resizers"
      :class="{
        ['show-for-dev']: resizer.showForDev,
        shrinkable: resizer.shrinkable,
      }"
    >
      <div
        v-for="({ className }, key) in resizer.parts"
        :key="key"
        :class="`${className}`"
        @mousedown="(event) => mouseDownResize(event, key)"
      ></div>
    </div>
  </div>
</template>

<script>
const sizeConstants = (() => {
  const imgInitCoef = 32; // 最小サイズをつくるための係数
  const imgInit = {
    width: 16 * imgInitCoef,
    height: 9 * imgInitCoef,
  };
  const besel = {
    left: {
      width: 14,
    },
    right: {
      width: 14,
    },
    top: {
      height: 28,
    },
    bottom: {
      height: 38,
    },
  };

  const wholeInit = {
    width: besel.left.width + imgInit.width + besel.right.width,
    height: besel.top.height + imgInit.height + besel.bottom.height,
  };

  return {
    besel,
    wholeInit,
  };
})();

const CalcBasedOnAspectRatio = ({ width, height }) => {
  const greatestCommonDivisor = (x, y) => {
    if (y === 0) return x;
    return greatestCommonDivisor(y, x % y);
  };

  const gcd = greatestCommonDivisor(width, height);
  const aspectRatio = {
    width: width / gcd,
    height: height / gcd,
  };

  return {
    heightByWidth: (width) => (width / aspectRatio.width) * aspectRatio.height,
    widthByHeight: (height) =>
      (height / aspectRatio.height) * aspectRatio.width,
  };
};

const calcBasedOnAspectRatio = CalcBasedOnAspectRatio(sizeConstants.wholeInit);

const [
  px,
  // ms
] = ((...suffixes) => suffixes.map((suffix) => (value) => value + suffix))(
  "px",
  "ms",
);

const getBrowserWindowCenter = () => {
  const { innerWidth, innerHeight } = window;
  const x = innerWidth / 2 || 0;
  const y = innerHeight / 2 || 0;
  return {
    x,
    y,
  }; // { x: number; y: number; }
};

const calcDefaultPosition = (
  vc, // VueComponent
) => {
  const getBoundingClientRectOfParent = (vc) => {
    const bRect = document.body.getBoundingClientRect();
    if (!vc) return bRect;
    if (!vc.$el) return bRect;
    if (!vc.$el.parentNode) return bRect;
    const pRect = vc.$el.parentNode.getBoundingClientRect();
    return pRect;
  };
  const { x: fromParentX, y: fromParentY } = getBoundingClientRectOfParent(vc);
  const { x: centerX, y: centerY } = getBrowserWindowCenter();
  const { width, height } = sizeConstants.wholeInit;
  const x = centerX - width / 2 - fromParentX;
  const y = centerY - height / 2 - fromParentY;
  return {
    x,
    y,
  }; // { x: number; y: number; }
};

// /src/views/enterprise/live-map/layers/radar-grid/components/RgZoomPic.vue
// を元にドラッグで動かす処理とリサイズする処理を抜き出し、初期位置などを既存のRgCameraに合わせて調整した
export default {
  name: "DraggableResizer",
  props: {
    initialPosition: {
      type: Object,
      default: () => calcDefaultPosition(),
    },
  },
  data() {
    const { initialPosition } = this;
    return {
      isReady: false, // 位置調整が終わってから表示する
      position: initialPosition,
      move: {
        offsets: {
          position: {
            x: 0,
            y: 0,
          },
        },
      },

      sizes: {
        width: sizeConstants.wholeInit.width,
        height: sizeConstants.wholeInit.height,
      },
      resizing: false,
      resizer: {
        showForDev: false,
        parts: {
          topLeft: {
            className: "top-left",
            cursor: {
              default: "nw-resize",
              shrinkable: "nwse-resize",
            },
          },
          top: {
            className: "top",
            cursor: {
              default: "n-resize",
              shrinkable: "ns-resize",
            },
          },
          topRight: {
            className: "top-right",
            cursor: {
              default: "ne-resize",
              shrinkable: "nesw-resize",
            },
          },
          right: {
            className: "right",
            cursor: {
              default: "e-resize",
              shrinkable: "ew-resize",
            },
          },
          bottomRight: {
            className: "bottom-right",
            cursor: {
              default: "se-resize",
              shrinkable: "nwse-resize",
            },
          },
          bottom: {
            className: "bottom",
            cursor: {
              default: "s-resize",
              shrinkable: "ns-resize",
            },
          },
          bottomLeft: {
            className: "bottom-left",
            cursor: {
              default: "sw-resize",
              shrinkable: "nesw-resize",
            },
          },
          left: {
            className: "left",
            cursor: {
              default: "w-resize",
              shrinkable: "ew-resize",
            },
          },
        },
        activePart: {},
        activePartResizeStaticArgs: {},
        shrinkable: false,
        offsets: {
          position: {
            x: 0,
            y: 0,
          },
          sizes: {
            width: 0,
            height: 0,
          },
        },
      },
    };
  },

  computed: {
    cssVars() {
      return {
        "--left": px(this.position.x),
        "--top": px(this.position.y),
        "--width": px(this.sizes.width),
        "--height": px(this.sizes.height),
        "--besel-left-width": px(sizeConstants.besel.left.width),
        "--besel-right-width": px(sizeConstants.besel.right.width),
        "--besel-top-height": px(sizeConstants.besel.top.height),
        "--besel-bottom-height": px(sizeConstants.besel.bottom.height),
      };
    },
  },

  methods: {
    forceCursorToDocument(cursor) {
      const { style } = document.documentElement;
      style.setProperty("cursor", cursor, "important");
      style.setProperty("pointer-events", "none", "important");
    },

    forceCursorToDocumentByPart(shrinkable) {
      const { cursor: resizerPartCursor } = this.resizer.activePart;
      const cursor = shrinkable
        ? resizerPartCursor.shrinkable
        : resizerPartCursor.default;
      this.forceCursorToDocument(cursor);
    },

    // NOTE: getComputedStyle()の使用例として残しておくが、計算によらない行き当たりばったりな実装を呼び込みやすいので非推奨
    // forceCursorToDocumentByElement(element) {
    //   this.forceCursorToDocument(window.getComputedStyle(element).cursor);
    // },

    mouseDownResize(event, partKey) {
      this.resizing = true;
      this.resizer.activePart = this.resizer.parts[partKey];
      const activePartResizeStaticArgs =
        this.partKeyToMouseMoveResizeStaticArgs(partKey);
      this.resizer.activePartResizeStaticArgs = activePartResizeStaticArgs;
      document.addEventListener("mousemove", this.mouseMoveResize);
      document.addEventListener("mouseup", this.mouseUpResize);
      this.forceCursorToDocumentByPart(this.resizer.shrinkable);

      this.resizer.offsets.sizes.width = this.sizes.width;
      this.resizer.offsets.sizes.height = this.sizes.height;
      this.resizer.offsets.position.x = event.clientX;
      this.resizer.offsets.position.y = event.clientY;
    },

    mouseMoveResizeCore(
      event,
      { horizontal, positionAdding, positionFixer = () => {} } = {
        horizontal: false,
        positionAdding: false,
        positionFixer: ({ x, y }) => {
          this.position.x += x;
          this.position.y += y;
        },
      },
    ) {
      const { width: offsetWidth, height: offsetHeight } =
        this.resizer.offsets.sizes;
      const { x: offsetX, y: offsetY } = this.resizer.offsets.position;
      const { clientX, clientY } = event;
      const { width: initWidth, height: initHeight } = sizeConstants.wholeInit;
      const offsetSize = horizontal ? offsetWidth : offsetHeight;
      const movedSize = horizontal ? clientX - offsetX : clientY - offsetY;
      const nextSizeCalced = offsetSize + movedSize * (positionAdding ? 1 : -1);
      const minLimit = horizontal ? initWidth : initHeight;

      const shrinkable = nextSizeCalced >= minLimit;
      const nextSizeFixed = shrinkable ? nextSizeCalced : minLimit;
      this.resizer.shrinkable = shrinkable;
      this.forceCursorToDocumentByPart(shrinkable);

      const { heightByWidth, widthByHeight } = calcBasedOnAspectRatio;
      const calcAnother = horizontal ? heightByWidth : widthByHeight;
      const nextSizeAnother = calcAnother(nextSizeFixed);
      const nextWidth = horizontal ? nextSizeFixed : nextSizeAnother;
      const nextHeight = horizontal ? nextSizeAnother : nextSizeFixed;
      const fixingX = nextWidth - this.sizes.width;
      const fixingY = nextHeight - this.sizes.height;

      this.sizes.width = nextWidth;
      this.sizes.height = nextHeight;
      positionFixer({ x: fixingX, y: fixingY });
    },

    // NOTE: ほんとはdata関数戻り値として宣言するのが適切ではあるものの、関数を含むので抽象アルゴリズムと近い視野に置いた
    partKeyToMouseMoveResizeStaticArgs(partKey) {
      return {
        topLeft: {
          horizontal: true,
          positionAdding: false,
          positionFixer: ({ x, y }) => {
            this.position.x -= x;
            this.position.y -= y;
          },
        },
        top: {
          horizontal: false,
          positionAdding: false,
          positionFixer: ({ x, y }) => {
            this.position.x -= x / 2;
            this.position.y -= y;
          },
        },
        topRight: {
          horizontal: true,
          positionAdding: true,
          positionFixer: ({ y }) => {
            this.position.y -= y;
          },
        },
        right: {
          horizontal: true,
          positionAdding: true,
          positionFixer: ({ y }) => {
            this.position.y -= y / 2;
          },
        },
        bottomRight: {
          horizontal: true,
          positionAdding: true,
        },
        bottom: {
          horizontal: false,
          positionAdding: true,
          positionFixer: ({ x }) => {
            this.position.x -= x / 2;
          },
        },
        bottomLeft: {
          horizontal: true,
          positionAdding: false,
          positionFixer: ({ x }) => {
            this.position.x -= x;
          },
        },
        left: {
          horizontal: true,
          positionAdding: false,
          positionFixer: ({ x, y }) => {
            this.position.x -= x;
            this.position.y -= y / 2;
          },
        },
      }[partKey];
    },

    mouseMoveResize(event) {
      this.mouseMoveResizeCore(event, this.resizer.activePartResizeStaticArgs);
    },

    mouseUpResize() {
      this.resizing = false;
      document.documentElement.style.cursor = "";
      document.documentElement.style.pointerEvents = "";
      document.removeEventListener("mousemove", this.mouseMoveResize);
      document.removeEventListener("mouseup", this.mouseUpResize);
    },

    // move
    mouseDownDrag(event) {
      document.addEventListener("mousemove", this.mouseMoveDrag);
      document.addEventListener("mouseup", this.mouseUpDrag);

      const { offsetLeft, offsetTop } = this.$refs.container;
      this.move.offsets.position.x = event.clientX - offsetLeft;
      this.move.offsets.position.y = event.clientY - offsetTop;
    },

    mouseMoveDrag(event) {
      const next = {
        x: event.clientX - this.move.offsets.position.x,
        y: event.clientY - this.move.offsets.position.y,
      };
      this.position.x = next.x;
      this.position.y = next.y;
    },

    mouseUpDrag() {
      document.removeEventListener("mousemove", this.mouseMoveDrag);
      document.removeEventListener("mouseup", this.mouseUpDrag);
    },

    setInitialPosition() {
      this.position = calcDefaultPosition(this);
      this.isReady = true;
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.setInitialPosition();
    });
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

@mixin flex-centering() {
  display: flex;
  justify-content: center;
  align-items: center;
}

$z-index-max: 2147483647;

.resizer-container {
  position: absolute;

  z-index: 12;
  &.resizing {
    // 階層コントロールがないのであんまり意味ないけど・・・
    z-index: $z-index-max;
  }

  top: var(--top);
  left: var(--left);

  width: var(--width);
  height: var(--height);

  background: #021431;
  border-radius: 5px;
  user-select: none;

  .main {
    z-index: 0;
    cursor: move;
    display: grid;
    grid-template-rows: var(--besel-top-height) 1fr var(--besel-bottom-height);
    grid-template-columns: var(--besel-left-width) 1fr var(--besel-right-width);
    width: 100%;
    height: 100%;
    position: absolute;

    .besel-left {
      grid-column: 1/2;
      grid-row: 1/4;
    }
    .besel-right {
      grid-column: 3/4;
      grid-row: 1/4;
    }
  }

  // grid-columnとgrid-rowのmapへの定義はそのままだと分数リテラルと解釈して割り算されてしまうため
  // (最終的にCSSでgrid子要素の定義だと解釈されるまでに計算が先行するんだろう)、
  // 文字列として定義してmap-get関数でリテラル出力する
  $parts: (
    top-left: (
      grid-column: "1/3",
      // 1 - 3 / 6
      grid-row: "1/3",
      cursor: (
        default: nw-resize,
        shrinkable: nwse-resize,
      ),
    ),
    top: (
      grid-column: "3/4",
      // 3 - 4
      grid-row: "1/2",
      cursor: (
        default: n-resize,
        shrinkable: ns-resize,
      ),
    ),
    top-right: (
      grid-column: "4/6",
      // 4 - 6
      grid-row: "1/3",
      cursor: (
        default: ne-resize,
        shrinkable: nesw-resize,
      ),
    ),
    right: (
      grid-column: "5/6",
      grid-row: "3/4",
      cursor: (
        default: e-resize,
        shrinkable: ew-resize,
      ),
    ),
    bottom-right: (
      grid-column: "4/6",
      grid-row: "4/6",
      cursor: (
        default: se-resize,
        shrinkable: nwse-resize,
      ),
    ),
    bottom: (
      grid-column: "3/4",
      grid-row: "5/6",
      cursor: (
        default: s-resize,
        shrinkable: ns-resize,
      ),
    ),
    bottom-left: (
      grid-column: "1/3",
      grid-row: "4/6",
      cursor: (
        default: sw-resize,
        shrinkable: nesw-resize,
      ),
    ),
    left: (
      grid-column: "1/2",
      grid-row: "3/4",
      cursor: (
        default: w-resize,
        shrinkable: ew-resize,
      ),
    ),
  );
  .resizers {
    z-index: 10;
    $border-width: 5px;
    $corner-width: 6px;
    $subtract-from-full: ($border-width + $corner-width) * 2;
    position: absolute;
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-columns:
      $border-width $corner-width calc(100% - #{$subtract-from-full})
      $corner-width $border-width;
    grid-template-rows:
      $border-width $corner-width calc(100% - #{$subtract-from-full})
      $corner-width $border-width;
    pointer-events: none;
    & > * {
      pointer-events: auto;
      background: transparent;
    }

    // 子要素1つごとに全階層分の定義を展開するのではなく、階層定義の見通しを優先して「あるべき階層で」毎回展開する
    @each $key, $part in $parts {
      .#{$key} {
        grid-column: #{map-get($part, grid-column)};
        grid-row: #{map-get($part, grid-row)};
      }
    }

    &.show-for-dev {
      & > * {
        opacity: 0.5;
        background: yellow;
      }
    }
  }
  &.resizing {
    pointer-events: none;
  }
  &:not(.resizing) {
    .resizers {
      // 子要素1つごとに全階層分の定義を展開するのではなく、階層定義の見通しを優先して「あるべき階層で」毎回展開する
      @each $key, $part in $parts {
        $cursor: map-get($part, cursor);
        .#{$key} {
          cursor: map-get($cursor, default);
        }
      }
      &.shrinkable {
        // 子要素1つごとに全階層分の定義を展開するのではなく、階層定義の見通しを優先して「あるべき階層で」毎回展開する
        @each $key, $part in $parts {
          $cursor: map-get($part, cursor);
          .#{$key} {
            cursor: map-get($cursor, shrinkable);
          }
        }
      }
    }
  }
}
</style>
